import type {
  AuditEvent, ChangeStatus, Comment, CommentAnchor, DocumentRecord, Draft, ID, Project, ProposedChange, Snapshot, Story, Thread, User,
} from '@proofroom/schema';
import type { ConnectionState, EventHandler, EventSourceLike, EventType } from '../api/events';
import type { ChangeConflict, SendChangesResult, UserRef } from '../api/endpoints';

/** In-memory stand-in for the server contract in docs/API.md, used when VITE_FIXTURE=1. */

interface Reply { status: number; body: unknown }

const SEED_USERS: Array<User & { password: string }> = [
  { id: 'u-admin', email: 'chris.southgate@chs.agency', name: 'Chris Southgate', role: 'admin', orgId: 'org-chs', emailEnabled: true, toursSeen: null, createdAt: '2026-08-01T09:00:00Z', password: 'proofroom-admin' },
  { id: 'u-designer', email: 'designer@chs.agency', name: 'CHS Designer', role: 'designer', orgId: 'org-chs', emailEnabled: true, toursSeen: null, createdAt: '2026-08-01T09:00:00Z', password: 'proofroom-designer' },
  { id: 'u-client', email: 'client@example.com', name: 'NGED Client', role: 'client', orgId: 'org-nged', emailEnabled: true, toursSeen: null, createdAt: '2026-08-01T09:00:00Z', password: 'proofroom-client' },
];

const SESSION_KEY = 'proofroom.mock.session';

export interface MockApi {
  handle(method: string, url: string, body?: unknown): Promise<Reply>;
  events: EventSourceLike;
  /** dev helper: simulate the plugin publishing a new draft of the document */
  publishDraft(label?: string): Draft;
  state: { snapshot: Snapshot; drafts: Draft[]; threads: Thread[]; changes: ProposedChange[]; audit: AuditEvent[] };
}

let counter = 1;
const nid = (p: string) => `${p}-${(counter++).toString(36)}${Date.now().toString(36).slice(-3)}`;
const now = () => new Date().toISOString();

export function createMockApi(snapshot: Snapshot): MockApi {
  const project: Project = { id: 'proj-nged', orgId: 'org-chs', name: 'NGED ED3', clientOrgId: 'org-nged', createdAt: '2026-08-01T09:00:00Z' };
  const document: DocumentRecord = { id: snapshot.documentId, projectId: project.id, name: snapshot.documentName, currentDraftId: 'draft-1', createdAt: '2026-08-20T09:00:00Z' };
  const drafts: Draft[] = [{ id: 'draft-1', documentId: document.id, number: 1, label: 'First proof', snapshotId: 'snap-1', source: 'plugin', createdBy: 'u-designer', createdAt: snapshot.exportedAt, publishedAt: snapshot.exportedAt, notes: 'Exported from InDesign for client review.' }];
  const threads: Thread[] = [];
  const changes: ProposedChange[] = [];
  const audit: AuditEvent[] = [];
  const handlers = new Set<EventHandler>();
  const stateHandlers = new Set<(s: ConnectionState) => void>();

  const emit = (type: EventType, data: unknown) => { for (const h of handlers) h(type, data); };
  const log = (actorId: ID | undefined, action: string, entity: string, entityId: ID, extra: Partial<AuditEvent> = {}) => {
    audit.unshift({ id: nid('ae'), ts: now(), actorId, actorType: actorId ? 'user' : 'system', action, entity, entityId, documentId: document.id, ...extra });
  };

  let sessionUserId: ID | undefined = (() => { try { return localStorage.getItem(SESSION_KEY) ?? undefined; } catch { return undefined; } })();
  const setSession = (id?: ID) => { sessionUserId = id; try { if (id) localStorage.setItem(SESSION_KEY, id); else localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ } };
  const publicUser = (u: User & { password?: string }): User => { const { password: _p, ...rest } = u; return rest; };
  const me = () => SEED_USERS.find((u) => u.id === sessionUserId);
  const err = (status: number, code: string, message: string): Reply => ({ status, body: { error: { code, message } } });
  /** Resolve an author id the way the real server does: id, name, role, never the email. */
  const ref = (id: ID | undefined): UserRef | undefined => {
    const u = id ? SEED_USERS.find((x) => x.id === id) : undefined;
    return u ? { id: u.id, name: u.name, role: u.role } : undefined;
  };
  const withAuthors = (t: Thread) => ({
    ...t,
    createdByUser: ref(t.createdBy),
    resolvedByUser: ref(t.resolvedBy),
    comments: t.comments.map((c) => ({ ...c, author: ref(c.authorId) })),
  });
  const withChangeAuthors = (c: ProposedChange) => ({ ...c, author: ref(c.authorId), decidedByUser: ref(c.decidedBy) });
  const withActor = (e: AuditEvent) => ({ ...e, actor: ref(e.actorId) });

  /**
   * A still-proposed change of the sender's own on a touched paragraph is superseded; one by
   * someone else is left alone and reported back in `conflicts`, matching the server (see
   * packages/server/src/changes/diff.ts planChanges).
   */
  const sendChanges = (draft: Draft, stories: Story[], author: User): SendChangesResult => {
    const created: ProposedChange[] = [];
    const conflicts: ChangeConflict[] = [];
    const touch = (storyId: ID, paraId: ID) => {
      for (const c of changes) {
        if (c.storyId !== storyId || c.paraId !== paraId || c.status !== 'proposed') continue;
        if (c.authorId === author.id) c.status = 'superseded';
        else conflicts.push({ paraId, changeId: c.id, authorName: ref(c.authorId)?.name ?? 'Unknown' });
      }
    };
    for (const story of stories) {
      const original = snapshot.stories.find((s) => s.id === story.id);
      if (!original) continue;
      const before = new Map(original.paragraphs.map((p) => [p.id, p]));
      const seen = new Set<ID>();
      let prevId: ID | undefined;
      for (const p of story.paragraphs) {
        seen.add(p.id);
        const b = before.get(p.id);
        let kind: ProposedChange['kind'] | undefined;
        if (!b) kind = 'insert';
        else if (b.text !== p.text) kind = 'replace';
        if (kind) {
          touch(story.id, p.id);
          const ch: ProposedChange = { id: nid('chg'), documentId: document.id, draftId: draft.id, storyId: story.id, paraId: p.id, kind, before: b?.text ?? '', after: p.text, styleId: p.styleId, authorId: author.id, createdAt: now(), status: 'proposed' };
          // the server stores the runs of `after` so an accepted edit keeps its character styling
          if (p.runs.some((r) => r.charStyleId || r.overrides)) ch.afterRuns = p.runs;
          if (kind === 'insert' && prevId) ch.afterParaId = prevId;
          changes.unshift(ch); created.push(ch);
          log(author.id, 'change.propose', 'change', ch.id, { draftId: draft.id, before: { text: ch.before }, after: { text: ch.after } });
        }
        prevId = p.id;
      }
      for (const b of original.paragraphs) {
        if (seen.has(b.id)) continue;
        touch(story.id, b.id);
        const ch: ProposedChange = { id: nid('chg'), documentId: document.id, draftId: draft.id, storyId: story.id, paraId: b.id, kind: 'delete', before: b.text, after: '', authorId: author.id, createdAt: now(), status: 'proposed' };
        changes.unshift(ch); created.push(ch);
        log(author.id, 'change.propose', 'change', ch.id, { draftId: draft.id, before: { text: b.text }, after: null });
      }
    }
    for (const c of created) setTimeout(() => emit('change.created', c), 0);
    return { created: created.map(withChangeAuthors), conflicts };
  };

  /**
   * GET stories/effective: the snapshot with this draft's accepted and applied changes folded in,
   * oldest first, skipping the ones InDesign would refuse (text moved on, insert with no anchor).
   * The server does this properly in packages/server/src/changes/apply.ts; this is the fixture's
   * own stand-in, as sendChanges above is for the diff.
   */
  const effectiveStories = (draft: Draft): Story[] => {
    const decided = [...changes].reverse().filter((c) => c.draftId === draft.id && (c.status === 'accepted' || c.status === 'applied'));
    return snapshot.stories.map((story) => {
      const mine = decided.filter((c) => c.storyId === story.id);
      if (!mine.length) return story;
      const paragraphs = [...story.paragraphs];
      const at = (id: ID | undefined) => (id ? paragraphs.findIndex((p) => p.id === id) : -1);
      for (const c of mine) {
        const i = at(c.paraId);
        const made = { id: c.paraId, text: c.after, runs: c.afterRuns ?? [{ start: 0, end: c.after.length }] };
        if (c.kind === 'insert') {
          const anchor = c.afterParaId ? at(c.afterParaId) : -1;
          if (i < 0 && anchor >= 0) paragraphs.splice(anchor + 1, 0, { ...made, styleId: c.styleId ?? paragraphs[anchor].styleId });
        } else if (i >= 0 && paragraphs[i].text === c.before) {
          if (c.kind === 'delete') paragraphs.splice(i, 1);
          else paragraphs[i] = { ...paragraphs[i], ...made, styleId: c.styleId ?? paragraphs[i].styleId };
        }
      }
      return { ...story, paragraphs };
    });
  };

  const handle = async (method: string, url: string, body?: unknown): Promise<Reply> => {
    const u = new URL(url, 'http://mock');
    const path = u.pathname;
    const parts = path.split('/').filter(Boolean); // ['api', ...]
    const b = (body ?? {}) as Record<string, unknown>;
    await new Promise((r) => setTimeout(r, 30 + Math.random() * 60));

    if (method === 'POST' && path === '/api/auth/login') {
      const user = SEED_USERS.find((x) => x.email === b.email && x.password === b.password);
      if (!user) return err(401, 'invalid_credentials', 'Email or password is incorrect');
      setSession(user.id);
      log(user.id, 'auth.login', 'user', user.id);
      return { status: 200, body: publicUser(user) };
    }
    if (method === 'POST' && path === '/api/auth/logout') { setSession(undefined); return { status: 200, body: { ok: true } }; }
    const user = me();
    if (!user) return err(401, 'unauthenticated', 'Sign in to continue');
    if (path === '/api/auth/me') return { status: 200, body: publicUser(user) };
    if (method === 'POST' && path === '/api/me/tours') {
      if (!['home', 'viewer', 'guest'].includes(String(b.tour))) return err(400, 'bad_request', 'tour must be one of home, viewer, guest');
      user.toursSeen = { ...(user.toursSeen ?? {}), [String(b.tour)]: now() };
      return { status: 200, body: user.toursSeen };
    }
    if (path === '/api/projects' && method === 'GET') return { status: 200, body: [project] };
    if (parts[1] === 'projects' && parts[3] === 'documents') return parts[2] === project.id ? { status: 200, body: [document] } : err(404, 'not_found', 'No such project');

    if (parts[1] === 'documents') {
      if (parts[2] !== document.id) return err(404, 'not_found', 'No such document');
      const sub = parts[3];
      if (!sub) return { status: 200, body: { ...document, drafts } };
      if (sub === 'drafts') return { status: 200, body: drafts };
      if (sub === 'changes') {
        const status = u.searchParams.get('status') as ChangeStatus | null;
        return { status: 200, body: (status ? changes.filter((c) => c.status === status) : changes).map(withChangeAuthors) };
      }
      if (sub === 'threads' && method === 'GET') {
        const d = u.searchParams.get('draftId');
        return { status: 200, body: (d ? threads.filter((t) => t.draftId === d) : threads).map(withAuthors) };
      }
      if (sub === 'threads' && method === 'POST') {
        const anchor = b.anchor as CommentAnchor;
        const t: Thread = { id: nid('thr'), documentId: document.id, draftId: String(b.draftId), anchor, status: 'open', createdBy: user.id, createdAt: now(), comments: [] };
        t.comments.push({ id: nid('cmt'), threadId: t.id, authorId: user.id, body: String(b.body ?? ''), createdAt: now() });
        threads.unshift(t);
        log(user.id, 'thread.create', 'thread', t.id, { draftId: t.draftId, after: { anchor, body: b.body } });
        setTimeout(() => emit('thread.created', withAuthors(t)), 0);
        return { status: 200, body: withAuthors(t) };
      }
      if (sub === 'audit') {
        const limit = Number(u.searchParams.get('limit') ?? 100);
        const before = u.searchParams.get('before');
        return { status: 200, body: audit.filter((e) => !before || e.ts < before).slice(0, limit).map(withActor) };
      }
    }

    if (parts[1] === 'drafts') {
      const draft = drafts.find((d) => d.id === parts[2]);
      if (!draft) return err(404, 'not_found', 'No such draft');
      if (!parts[3]) return { status: 200, body: draft };
      if (parts[3] === 'snapshot') return { status: 200, body: snapshot };
      if (parts[3] === 'stories' && parts[4] === 'effective') return { status: 200, body: effectiveStories(draft) };
      if (parts[3] === 'changes' && parts[4] === 'send') {
        return { status: 200, body: sendChanges(draft, (b.stories as Story[]) ?? [], user) };
      }
      if (parts[3] === 'approve' || parts[3] === 'unapprove') {
        if (user.role !== 'client' && user.role !== 'admin') return err(403, 'forbidden', 'Only a client or admin can approve a draft');
        if (parts[3] === 'approve') {
          if (draft.approvedAt) return err(409, 'conflict', 'draft already approved');
          draft.approvedBy = user.id; draft.approvedAt = now();
          log(user.id, 'draft.approve', 'draft', draft.id, { draftId: draft.id, after: { approvedBy: draft.approvedBy } });
        } else {
          if (!draft.approvedAt) return err(409, 'conflict', 'draft not approved');
          delete draft.approvedBy; delete draft.approvedAt;
          log(user.id, 'draft.unapprove', 'draft', draft.id, { draftId: draft.id });
        }
        setTimeout(() => emit('draft.updated', draft), 0);
        return { status: 200, body: draft };
      }
    }

    if (parts[1] === 'changes') {
      const c = changes.find((x) => x.id === parts[2]);
      if (!c) return err(404, 'not_found', 'No such change');
      if (user.role === 'client') return err(403, 'forbidden', 'Only designers can decide changes');
      const decision = parts[3];
      if (decision === 'accept' || decision === 'reject') {
        if (decision === 'reject' && !b.note) return err(400, 'note_required', 'A note is required when rejecting');
        const before = { ...c };
        c.status = decision === 'accept' ? 'accepted' : 'rejected';
        c.decidedBy = user.id; c.decidedAt = now();
        if (b.note) c.decisionNote = String(b.note);
        log(user.id, `change.${decision}`, 'change', c.id, { draftId: c.draftId, before: { status: before.status }, after: { status: c.status, note: c.decisionNote } });
        setTimeout(() => emit('change.decided', withChangeAuthors(c)), 0);
        return { status: 200, body: withChangeAuthors(c) };
      }
    }

    if (parts[1] === 'threads') {
      const t = threads.find((x) => x.id === parts[2]);
      if (!t) return err(404, 'not_found', 'No such thread');
      if (parts[3] === 'comments') {
        const c: Comment = { id: nid('cmt'), threadId: t.id, authorId: user.id, body: String(b.body ?? ''), createdAt: now() };
        t.comments.push(c);
        log(user.id, 'comment.create', 'comment', c.id, { after: { body: c.body } });
        setTimeout(() => emit('comment.created', { ...c, author: ref(user.id) }), 0);
        return { status: 200, body: { ...c, author: ref(user.id) } };
      }
      if (parts[3] === 'resolve' || parts[3] === 'reopen') {
        const before = t.status;
        t.status = parts[3] === 'resolve' ? 'resolved' : 'open';
        if (t.status === 'resolved') { t.resolvedBy = user.id; t.resolvedAt = now(); } else { delete t.resolvedBy; delete t.resolvedAt; }
        log(user.id, `thread.${parts[3]}`, 'thread', t.id, { before: { status: before }, after: { status: t.status } });
        setTimeout(() => emit('thread.updated', withAuthors(t)), 0);
        return { status: 200, body: withAuthors(t) };
      }
    }
    return err(404, 'not_found', `No mock route for ${method} ${path}`);
  };

  const publishDraft = (label = 'Republished from InDesign'): Draft => {
    const n = drafts.length + 1;
    const d: Draft = { id: `draft-${n}`, documentId: document.id, number: n, label, snapshotId: `snap-${n}`, source: 'plugin', createdBy: 'u-designer', createdAt: now(), publishedAt: now() };
    drafts.push(d);
    document.currentDraftId = d.id;
    log('u-designer', 'draft.publish', 'draft', d.id, { draftId: d.id, after: { number: n, label } });
    emit('draft.published', d);
    return d;
  };

  return {
    handle,
    publishDraft,
    state: { snapshot, drafts, threads, changes, audit },
    events: {
      subscribe(handler, onState) {
        handlers.add(handler); stateHandlers.add(onState);
        setTimeout(() => onState('open'), 10);
        return () => { handlers.delete(handler); stateHandlers.delete(onState); };
      },
    },
  };
}
